/**
 * CoPilotLauncher — the floating "Ask Tonnie" button (BL-217).
 *
 * The discoverability fix: the old launcher was a bare `Sparkles` icon lost among
 * five identical header ghost-buttons. This is a persistent, LABELED floating
 * action button (mascot + "Ask Tonnie") pinned bottom-right — the Intercom/
 * assistant-widget pattern — so users always know where the Companion is.
 *
 * Presentational only: the panel's open state + toggle are app-specific
 * (`useCoPilotSidebar` lives per app), so they come in as props. Hidden while the
 * panel is open (the panel owns its own close). On narrow screens it collapses to
 * just the mascot disc.
 */

import { HeroAvatar } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { cn } from '@tonnie/ui';

/**
 * Room a page leaves at its bottom so the launcher never covers its last
 * controls (a Delete button, a switch, a card's action) — the launcher sits
 * `bottom-5` and is ~44px tall. Every layout that mounts the launcher pads its
 * content with this.
 */
export const COPILOT_LAUNCHER_CLEARANCE = 'pb-24';

/**
 * Where another floating control sits so it stacks ABOVE the launcher instead
 * of on top of it (same right edge, one launcher higher).
 */
export const COPILOT_LAUNCHER_STACKED = 'bottom-20 right-5';

export interface CoPilotLauncherProps {
  /** Whether the Companion panel is currently open (the FAB hides while open). */
  open: boolean;
  /** Open the Companion panel. */
  onOpen: () => void;
  className?: string;
}

export function CoPilotLauncher({ open, onOpen, className }: CoPilotLauncherProps) {
  const { t } = useTranslation();
  if (open) return null;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="copilot-launcher"
      aria-label={t('copilot.launcher.label')}
      className={cn(
        'group fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full',
        'border border-copilot-border bg-copilot-surface text-copilot-foreground',
        'py-1.5 pl-1.5 pr-2 shadow-lg shadow-copilot-accent/25 sm:pr-4',
        'transition-transform duration-150 hover:scale-105 active:scale-95',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copilot-accent focus-visible:ring-offset-2',
        className
      )}
    >
      <span className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-copilot-accent/10">
        <HeroAvatar hero="copilot" size="sm" className="size-7" />
      </span>
      <span className="hidden pr-1 text-sm font-semibold sm:inline">
        {t('copilot.launcher.label')}
      </span>
    </button>
  );
}

export default CoPilotLauncher;
